/**
 * Copyright IBM Corp. 2021, 2024
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */
import React from "react";
import PropTypes from "prop-types";
import debounce from "lodash/debounce";
import { Editor } from "@tinymce/tinymce-react";
import "tinymce/tinymce";
import "tinymce/themes/silver";
import "tinymce/plugins/autolink";
import "tinymce/plugins/lists";
import "tinymce/plugins/link";
import "tinymce/plugins/image";
import "tinymce/plugins/charmap";
import "tinymce/plugins/preview";
import "tinymce/plugins/anchor";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/insertdatetime";
import "tinymce/plugins/table";
import 'tinymce/models/dom/model';
import "./TriNoteIcons";
import { getEditorLanguage } from "./TriNoteLanguages";
import { TinyMceContentStyles } from "./TriNoteStyles";
import { getTriNoteAvailableFonts } from "./TriNoteFonts";

const DEFAULT_FONTS = `fonts Andale Mono="andale mono";Arial=arial;Arial Black="arial black";Comic Sans MS="comic sans ms";Courier New="courier new", courier;Georgia=georgia;IBM Plex Sans="IBM Plex Sans";Impact=Impact;Roboto=Roboto;Tahoma=tahoma;Times New Roman="times new roman",times;Trebuchet MS="trebuchet ms";Verdana=verdana;Webdings=webdings;Wingdings=wingdings;`;

/**
 * A React component that renders a TinyMCE editor for editing and displaying TRIRIGA note fields.
 */
export default class TriNote extends React.PureComponent {
  static propTypes = {
    /**
     * If true, sets the height of readonly note editor to the height of the note field content. This property is ignored if the readonly property is false.
     */
    autoHeight: PropTypes.bool,
    /**
     * Set the  direction of text within the note editor. Possible values are: "ltr" or "rtl"
     */
    directionality: PropTypes.string,
    /**
     * The language in which the note editor will appear, including the toolbar buttons and menu items. By default, it will use the "US English" as the language of the note editor.
     * It is the value of the \_Language field from the CURRENT_USER data source.
     */
    language: PropTypes.string,
    /**
     * The handler that is called whenever the note value is changed. The handler function receives the updated note value.
     */
    onChange: PropTypes.func,
    /**
     * If true, indicates that the user cannot modify the value.
     */
    readonly: PropTypes.bool,
    /**
     * The note field value. It is the value of a note field returned by a data source.
     */
    value: PropTypes.string,

    /**
     * If true, it will focus the textarea by default.
     */
    autoFocus: PropTypes.bool,
  };

  static defaultProps = {
    value: "",
    readonly: false,
    language: null,
    directionality: "ltr",
    autoHeight: false,
    autoFocus: false,
  };

  state = {
    fonts: null,
  };

  constructor() {
    super();
    this.debounceHandleSetContent = debounce(this.handleSetContent, 50);
    this.cancelGetFonts = false;
  }

  async componentDidMount() {
    this.cancelGetFonts = false;
    let fonts = null;
    try {
      fonts = await getTriNoteAvailableFonts();
    } catch (error) {
      fonts = DEFAULT_FONTS;
    }
    if (!this.cancelGetFonts) {
      this.setState({ fonts });
    }
  }

  componentWillUnmount() {
    this.cancelGetFonts = true;
  }

  render() {
    const {
      value,
      readonly,
      language,
      directionality,
      autoHeight,
      autoFocus,
    } = this.props;
    
    const editorLanguage = getEditorLanguage(language);
    if (this.state.fonts === null) {
      return null;
    }
    return (
      <Editor
        value={value}
        init={{
          promotion: false,
          content_style: TinyMceContentStyles,
          font_family_formats: this.state.fonts,
          height: "100%",
          language:
            editorLanguage != null && editorLanguage !== ""
              ? editorLanguage
              : null,
          directionality,
          menubar: readonly ? false : "edit insert view format table",
          plugins: [
            "autolink", "lists", "link", "image", "charmap", "preview", "anchor",
             "searchreplace", "visualblocks",
             "insertdatetime", "table",
          ],
          toolbar: readonly
            ? false
            : `undo redo | fontfamily | fontsize | blocks | forecolor backcolor | bold italic underline strikethrough |
             numlist bullist | alignleft aligncenter alignright alignjustify |
              outdent indent `,
          skin: false,
          content_css: false,
          branding: false,
          elementpath: false,
          statusbar: false,
          table_style_by_css: true,
          setup: (editor) => {
            if (readonly && autoHeight) {
              editor.on("SetContent", this.debounceHandleSetContent);
            }
            editor.on("init", function (e) {
              if (autoFocus) {
                  editor.execCommand("mceFocus");
              }
            });
          },
        }}
        onEditorChange={this.handleEditorChange}
        disabled={readonly}
      />
    );
  }

  handleSetContent = (e) => {
    const editor = e.target;
    this.setEditorContainerHeight(editor);
    // Run a second time 1 second later
    setTimeout(this.setEditorContainerHeight.bind(this, editor), 1000);
  };

  setEditorContainerHeight(editor) {
    if (editor.getContainer() == null) {
      return;
    }
    const contentHeight = editor.getBody()?.scrollHeight;
    if (contentHeight > 0) {
      editor.getContainer().style.height = `${contentHeight + 60}px`;
    } else {
      editor.getContainer().style.display = "none";
    }
  }

  handleEditorChange = (content) => {
    if (this.props.onChange != null) {
      this.props.onChange(content);
    }
  };
}
